import { useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, Campo, Cartao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  enderecoDoWebhookDeEntrada,
  PREFIXO_DA_VARIAVEL_DE_SENHA,
  ROTULO_DO_ADAPTADOR,
  useAlterarCaixa,
  useCaixas,
  useCriarCaixa,
  useEquipesParaCanais,
  useGirarTokenDaCaixa,
  variavelDeSenhaValida,
  type CaixaDeEntrada,
} from "../../nucleo/canais";
import { quandoLegivel } from "../../nucleo/automacao";
import { CaixaDeMarcar, CampoDeEscolha } from "../automacao/apoio";
import { SegredoMostradoUmaVez } from "../automacao/SegredoMostradoUmaVez";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

const ADAPTADORES = [
  { valor: "webhook", rotulo: ROTULO_DO_ADAPTADOR.webhook },
  { valor: "imap", rotulo: ROTULO_DO_ADAPTADOR.imap },
] as const;

function enderecoDeEntrada(): string {
  return enderecoDoWebhookDeEntrada(window.location.origin);
}

function SituacaoDaCaixa({ caixa }: { caixa: CaixaDeEntrada }) {
  if (!caixa.ativa) return <Chip estado="neutro">Desativada</Chip>;
  if (caixa.ultimo_erro) return <Chip estado="erro">Com problema</Chip>;
  return <Chip estado="ok">Ativa</Chip>;
}

export function PainelDeCaixas() {
  const caixas = useCaixas();
  const equipes = useEquipesParaCanais();
  const criar = useCriarCaixa();
  const voltarParaHome = useVoltarParaHome();
  const alterar = useAlterarCaixa();
  const girar = useGirarTokenDaCaixa();

  const [adaptador, setAdaptador] = useState<"webhook" | "imap">("webhook");
  const [nome, setNome] = useState("");
  const [endereco, setEndereco] = useState("");
  const [servidor, setServidor] = useState("");
  const [porta, setPorta] = useState("993");
  const [usuario, setUsuario] = useState("");
  const [variavel, setVariavel] = useState(PREFIXO_DA_VARIAVEL_DE_SENHA);
  const [pasta, setPasta] = useState("INBOX");
  const [authserv, setAuthserv] = useState("");
  const [confiar, setConfiar] = useState(false);
  const [equipeId, setEquipeId] = useState("");
  const [categoria, setCategoria] = useState("");
  const [problema, setProblema] = useState<string | null>(null);
  const [chave, setChave] = useState<{ titulo: string; valor: string } | null>(null);

  function validar(): string | null {
    if (nome.trim().length < 2) return "Dê um nome à caixa.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(endereco.trim())) {
      return "Informe o endereço de e-mail da caixa, como suporte@empresa.com.br.";
    }
    if (adaptador === "imap") {
      if (!servidor.trim() || !usuario.trim()) return "Informe o servidor e o usuário do IMAP.";
      if (!variavelDeSenhaValida(variavel)) {
        return `O nome da variável da senha deve começar com ${PREFIXO_DA_VARIAVEL_DE_SENHA} e usar só letras maiúsculas, números e sublinhado.`;
      }
    }
    if (!confiar && !authserv.trim()) {
      return "Informe o identificador do servidor de entrada (authserv-id) para o portal conferir SPF, DKIM e DMARC — ou marque a caixa como de confiança.";
    }
    return null;
  }

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const erro = validar();
    setProblema(erro);
    if (erro) return;
    criar.mutate(
      {
        nome: nome.trim(),
        endereco: endereco.trim(),
        adaptador,
        autenticacao: confiar ? "confiar" : "exigir",
        authserv_id: authserv.trim() || null,
        equipe_id: equipeId || null,
        categoria: categoria.trim() || null,
        ...(adaptador === "imap"
          ? {
              imap_host: servidor.trim(),
              imap_porta: Number(porta) || 993,
              imap_usuario: usuario.trim(),
              imap_senha_variavel: variavel.trim(),
              imap_pasta: pasta.trim() || "INBOX",
              imap_ssl: true,
            }
          : {}),
      },
      {
        onSuccess: (criada) => {
          if (criada.token) {
            setChave({ titulo: `Chave de entrada da caixa ${criada.nome}`, valor: criada.token });
          }
          setNome("");
          setEndereco("");
          // A chave de entrada só aparece UMA vez: com ela na tela a pessoa fica, para copiá-la.
          if (!criada.token) voltarParaHome("Caixa de entrada criada.");
        },
      },
    );
  }

  const falha = criar.error ?? alterar.error ?? girar.error ?? caixas.error;

  return (
    <div>
      {chave ? (
        <>
          <SegredoMostradoUmaVez
            titulo={chave.titulo}
            valor={chave.valor}
            rotuloDoBotao="Copiar chave"
            aoFechar={() => setChave(null)}
            data-testid="chave-da-caixa"
          />
          <Aviso tom="marca" className="mb-4" data-testid="como-usar-a-chave">
            O provedor de e-mail de entrada envia cada mensagem, inteira, para{" "}
            <code>{enderecoDeEntrada()}</code> com o cabeçalho <code>Authorization: Bearer</code> e
            a chave acima.
          </Aviso>
        </>
      ) : null}

      <Cartao titulo="Nova caixa de entrada" className="mb-4">
        <p className="nota-rodape mb-3">
          Por webhook, o provedor de e-mail entrega cada mensagem ao portal. Por IMAP, o portal lê a
          caixa sozinho. Em ambos, o e-mail de quem não é cliente conhecido vai para a quarentena.
        </p>
        <form onSubmit={submeter} data-testid="formulario-de-caixa" noValidate>
          <CampoDeEscolha
            rotulo="Como as mensagens chegam"
            valor={adaptador}
            aoMudar={(valor) => setAdaptador(valor === "imap" ? "imap" : "webhook")}
            opcoes={ADAPTADORES}
          />
          <Campo rotulo="Nome da caixa" value={nome} onChange={(e) => setNome(e.target.value)} />
          <Campo
            rotulo="Endereço de e-mail da caixa"
            value={endereco}
            onChange={(e) => setEndereco(e.target.value)}
            placeholder="suporte@empresa.com.br"
            ajuda="Mensagens vindas deste mesmo endereço são ignoradas, para o portal não responder a si mesmo."
          />
          {adaptador === "imap" ? (
            <>
              <Campo rotulo="Servidor IMAP" value={servidor} onChange={(e) => setServidor(e.target.value)} />
              <Campo rotulo="Porta" value={porta} inputMode="numeric" onChange={(e) => setPorta(e.target.value)} />
              <Campo rotulo="Usuário do IMAP" value={usuario} onChange={(e) => setUsuario(e.target.value)} />
              <Campo
                rotulo="Variável de ambiente da senha"
                value={variavel}
                onChange={(e) => setVariavel(e.target.value.toUpperCase())}
                ajuda={`A senha nunca fica no portal: o nome da variável que a guarda no servidor, começando com ${PREFIXO_DA_VARIAVEL_DE_SENHA}.`}
              />
              <Campo rotulo="Pasta" value={pasta} onChange={(e) => setPasta(e.target.value)} />
            </>
          ) : null}
          <Campo
            rotulo="Identificador do servidor de entrada (authserv-id)"
            value={authserv}
            onChange={(e) => setAuthserv(e.target.value)}
            placeholder="mx.empresa.com.br"
            ajuda="Só o resultado de SPF, DKIM e DMARC escrito por este servidor vale como prova de quem mandou."
          />
          <CaixaDeMarcar
            rotulo="Confiar nesta caixa sem conferir SPF, DKIM e DMARC"
            marcada={confiar}
            aoMudar={setConfiar}
            ajuda="Use só se a caixa recebe de um relé interno. Mesmo assim, e-mail de agente da GEOvendas nunca entra sem autenticação de verdade."
          />
          <CampoDeEscolha
            rotulo="Equipe que recebe os chamados"
            valor={equipeId}
            aoMudar={setEquipeId}
            vazio="Nenhuma (fila geral)"
            opcoes={(equipes.data ?? []).map((e) => ({ valor: e.id, rotulo: e.nome }))}
          />
          <Campo rotulo="Categoria dos chamados (opcional)" value={categoria} onChange={(e) => setCategoria(e.target.value)} />
          {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
          {falha ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(falha)}</Aviso> : null}
          <Botao type="submit" disabled={criar.isPending}>{criar.isPending ? "Criando…" : "Criar caixa"}</Botao>
        </form>
      </Cartao>

      <section aria-labelledby="titulo-caixas">
        <h2 className="titulo-secao" id="titulo-caixas">Caixas de entrada</h2>
        {caixas.isPending ? <p role="status">Carregando caixas…</p> : null}
        {caixas.data && caixas.data.length === 0 ? <Aviso tom="marca">Nenhuma caixa cadastrada.</Aviso> : null}
        <div className="lista-de-itens" data-testid="lista-de-caixas">
          {caixas.data?.map((caixa) => (
            <Cartao key={caixa.id} titulo={caixa.nome} data-testid={`caixa-${caixa.nome}`} acessorio={<SituacaoDaCaixa caixa={caixa} />}>
              <p>
                <code>{caixa.endereco}</code> · {ROTULO_DO_ADAPTADOR[caixa.adaptador]}
              </p>
              <p className="nota-rodape">
                {caixa.recebidos_24h} mensagem(ns) nas últimas 24 horas ·{" "}
                {caixa.autenticacao === "exigir" ? "confere SPF, DKIM e DMARC" : "caixa de confiança"}
                {caixa.adaptador === "imap" && caixa.ultimo_ciclo_em
                  ? ` · última leitura em ${quandoLegivel(caixa.ultimo_ciclo_em)}`
                  : ""}
                {caixa.token_prefixo ? ` · chave ${caixa.token_prefixo}…` : ""}
              </p>
              {caixa.ultimo_erro ? <Aviso tom="erro" className="mt-2">{caixa.ultimo_erro}</Aviso> : null}
              <div className="flex gap-2 mt-2">
                <Botao
                  type="button"
                  variante="fantasma"
                  aria-label={`${caixa.ativa ? "Desativar" : "Ativar"} a caixa ${caixa.nome}`}
                  onClick={() => alterar.mutate({ id: caixa.id, corpo: { ativa: !caixa.ativa } })}
                >
                  {caixa.ativa ? "Desativar" : "Ativar"}
                </Botao>
                {caixa.adaptador === "webhook" ? (
                  <Botao
                    type="button"
                    variante="fantasma"
                    aria-label={`Trocar a chave da caixa ${caixa.nome}`}
                    onClick={() =>
                      girar.mutate(caixa.id, {
                        onSuccess: (girada) => {
                          if (girada.token) {
                            setChave({ titulo: `Nova chave da caixa ${girada.nome}`, valor: girada.token });
                          }
                        },
                      })
                    }
                  >
                    Trocar a chave
                  </Botao>
                ) : null}
              </div>
            </Cartao>
          ))}
        </div>
      </section>
    </div>
  );
}
